import type { IUser } from '@carboncopyco/types';
import { Spacer, TextCaption, UserAvatar } from '@carboncopyco/ui';
import { getUTCFromNow } from '@carboncopyco/utils';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import type React from 'react';
import { useEffect, useState } from 'react';
import ActivityExternalDescription from './ActivityItemExternalDescription';

dayjs.extend(isSameOrAfter);

export interface ExternalDescription {
  image: string;
  title: string;
  description?: string;
  onClick: () => void;
}

interface ActivityListItemProps {
  creator: IUser;
  title: React.ReactNode;
  workspaceName?: string;
  createTime: string;
  externalDescription?: ExternalDescription;
  isOverlayDemo?: boolean;
}

const minute = 60000;

const ActivityListItem = ({
  creator,
  title,
  workspaceName,
  createTime,
  externalDescription,
  isOverlayDemo,
}: ActivityListItemProps) => {
  const [timeFromNow, setTimeFromNow] = useState(getUTCFromNow(createTime));

  function displayCreatedTime() {
    return dayjs(createTime.split('.')[0])
      .add(1, 'minutes')
      .isSameOrAfter(new Date().toISOString().split('.')[0])
      ? 'Just now'
      : timeFromNow;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const currentFromNow = getUTCFromNow(createTime);
      setTimeFromNow(currentFromNow);
    }, minute);
    return () => clearInterval(interval);
  }, [createTime]);

  return (
    <div className="flex flex-col justify-start w-full flex-1 bg-ds-accent3-100 rounded-md p-5">
      <div className="flex flex-row items-center">
        <div>
          <UserAvatar user={creator} />
        </div>
        <Spacer amount={1.25} />
        <div>
          <div className="flex justify-start items-center">{title}</div>
          <TextCaption
            label={`${workspaceName} | ${displayCreatedTime()}`}
            color="text-ds-natural-200"
          />
        </div>
      </div>

      <div className="flex flex-col justify-center ml-12 pl-1">
        {externalDescription && (
          <ActivityExternalDescription
            {...externalDescription}
            isOverlayDemo={isOverlayDemo}
          />
        )}
      </div>
    </div>
  );
};

export default ActivityListItem;
